import { NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FuseModule } from '@fuse';
import { FuseConfigModule } from '@fuse/services/config';
import { appConfig } from './core/config/app.config';
import { CoreModule } from './core/core.module';
import { LayoutModule } from '@angular/cdk/layout';
import { mockApiServices } from './mock-api';
import { FuseMockApiModule } from '@fuse/lib/mock-api';
import { CookieService } from 'ngx-cookie-service';
import { HttpRequestInterceptor } from './interceptors/httprequest.interceptor';
import { PermissionsInterceptor } from './interceptors/permissions.interceptor';
import {
  AWSTokenAvailabilityInterceptor,
} from '@sbd-account-portal/app/interceptors/awstokenavailability.interceptor';
import { ENVIRONMENT_INJECTION_TOKEN } from '@sbd-doug-frontend/shared/lib/injections/environment-injection-token';
import { environment } from '@sbd-account-portal/app/environments/environment';
import { MainFuseDrawerComponent } from '@sbd-account-portal/app/modules/main-fuse-drawer/mainfusedrawer.component';
import { SHOPPING_CART_DRAWER_WRAPPER } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart-drawer.types';
import {
  SHOPPING_CART_PAYMENT_PROVIDER,
} from '@sbd-doug-frontend/shared/lib/shopping-cart/payment/shopping-cart-payment.types';
import { AccountPortalShoppingCartDrawerWrapperService } from './shopping-cart/shopping-cart-drawer-wrapper.service';
import { AccountPortalPaymentProviderService } from './shopping-cart/account-portal-payment-provider.service';
import { CURRENT_USER_TOKEN, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import { CONTACTS_PROVIDER_TOKEN } from '@sbd-doug-frontend/shared/lib/shared-services/shared-contacts.service';
import { AccountPortalContactsService } from './services/contacts.service';
import { UserService as AccountPortalUserService } from '@sbd-account-portal/app/core/user/user.service';
import { User } from './core/user/user.types';
import {
  QUOTE_REQUESTS_LIST_SERVICE_TOKEN
} from '@sbd-doug-frontend/shared/lib/shared-services/quote-requests-list.abstract.service';
import { QuoteRequestsListService } from './modules/quote/requests-list/quote-requests-list.service';
import { map } from 'rxjs';
import { computed } from '@angular/core';
import { LEGAL_TERM_PREVIEW_ADAPTER } from '@sbd-doug-frontend/shared/lib/shared-components/legal-term-previewer/legal-term-previewer.adapter';
import { AccountPortalLegalTermPreviewAdapter } from './modules/terms-review/legal-term-previewer.adapter';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule,
    HttpClientModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FuseModule,
    FuseConfigModule.forRoot(appConfig),
    FuseMockApiModule.forRoot(mockApiServices),
    CoreModule,
    LayoutModule,
    MainFuseDrawerComponent,
  ],
  providers: [
    // Http Interceptor(s) - Adds withCredentials to requests because of cookies.
    [
      { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: PermissionsInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: AWSTokenAvailabilityInterceptor, multi: true },
      { provide: ENVIRONMENT_INJECTION_TOKEN, useValue: environment },
      { provide: SHOPPING_CART_DRAWER_WRAPPER, useClass: AccountPortalShoppingCartDrawerWrapperService },
      { provide: SHOPPING_CART_PAYMENT_PROVIDER, useClass: AccountPortalPaymentProviderService },
      {
        provide: CURRENT_USER_TOKEN,
        deps: [AccountPortalUserService],
        useFactory: (svc: AccountPortalUserService): SharedUserService<import('./core/user/user.types').AccountUserInfo> => ({
          get: () =>
            svc.get().pipe(
              map((u: User | null) => {
                if (!u) return null;
                type Extended = User & {
                  first_name?: string | null;
                  last_name?: string | null;
                  user_id?: string | number | null;
                  principal_user_id?: string | number | null;
                  account_user_info?: import('./core/user/user.types').AccountUserInfo | null;
                };
                const ux = u as unknown as Extended;
                return {
                  id: ux.user_id ?? u.id ?? null,
                  name: u.name ?? null,
                  email: u.email ?? null,
                  phone: u.phone ?? null,
                  firstName: ux.first_name ?? null,
                  lastName: ux.last_name ?? null,
                  principal_user_id: ux.user_id ?? null,
                  // unify to shared user_info
                  user_info: (ux.account_user_info as unknown as import('@sbd-doug-frontend/shared/lib/types/current-user').SharedUserInfo) ?? null,
                  team_members: ux.team_members ?? null,
                };
              }),
            ),
          current: computed(() => {
            const u = svc.currentAccountUser();
            if (!u) return null;
            const ux = u as unknown as User & {
              first_name?: string | null;
              last_name?: string | null;
              user_id?: string | number | null;
              account_user_info?: import('./core/user/user.types').AccountUserInfo | null;
            };
            return {
              id: ux.user_id ?? u.id ?? null,
              name: u.name ?? null,
              email: u.email ?? null,
              phone: u.phone ?? null,
              firstName: (ux as any).first_name ?? null,
              lastName: (ux as any).last_name ?? null,
              principal_user_id: ux.user_id ?? null,
              // unify to shared user_info
              user_info: (ux.account_user_info as unknown as import('@sbd-doug-frontend/shared/lib/types/current-user').SharedUserInfo) ?? null,
              team_members: ux.team_members ?? null,
            };
          }),
        }),
      },
      { provide: CONTACTS_PROVIDER_TOKEN, useExisting: AccountPortalContactsService },
      { provide: QUOTE_REQUESTS_LIST_SERVICE_TOKEN, useExisting: QuoteRequestsListService },
    ],
    { provide: LEGAL_TERM_PREVIEW_ADAPTER, useClass: AccountPortalLegalTermPreviewAdapter },
    CookieService,
    Title,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
